import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Select,
  MenuItem,
  Tooltip,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  PersonAdd as PersonAddIcon,
  Assignment as TaskIcon,
  EventBusy as NoShowIcon,
  CalendarMonth as CalendarIcon,
  Insights as InsightsIcon,
  Campaign as CampaignIcon,
  ManageAccounts as OpsIcon,
  Place as PlaceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import apiClient from '../api/client';
import { formatCurrency } from '../utils/currency';

/*
  Clinic dashboard — three persona views (ops / owner / marketing) sharing one
  data bundle from GET /api/dashboard/ops. The bundle is tenant-scoped via the
  authenticated user's schema; only panels with non-empty data render.
*/

const PERSONAS = [
  { value: 'ops', label: 'Ops', icon: <OpsIcon fontSize="small" /> },
  { value: 'owner', label: 'Owner', icon: <InsightsIcon fontSize="small" /> },
  { value: 'marketing', label: 'Marketing', icon: <CampaignIcon fontSize="small" /> },
];

const ALL_LOCATIONS = '__all__';

/* ---------- shared helpers ---------- */

const num = (v) => Number(v ?? 0);
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);

const DeltaPill = ({ value, suffix = '%', positiveGood = true }) => {
  const theme = useTheme();
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const isPositive = value >= 0;
  const isGood = positiveGood ? isPositive : !isPositive;
  const color = isGood ? theme.palette.success.main : theme.palette.error.main;
  const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        bgcolor: alpha(color, 0.12),
        color,
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      <Icon sx={{ fontSize: '0.9rem' }} />
      {isPositive ? '+' : ''}
      {value}
      {suffix}
    </Box>
  );
};

const InsightCard = ({ title, subtitle, icon, accent, children, action }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        {icon && (
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(color, 0.14),
              color,
              width: 36,
              height: 36,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
  );
};

const KpiTile = ({ label, value, sub, delta, deltaSuffix, deltaPositiveGood, icon, accent }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: color,
          opacity: 0.7,
        }}
      />
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.5, fontWeight: 500 }}
          >
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {sub}
            </Typography>
          )}
          {delta !== undefined && delta !== null && !Number.isNaN(delta) && (
            <Box sx={{ mt: 1.25 }}>
              <DeltaPill value={delta} suffix={deltaSuffix} positiveGood={deltaPositiveGood} />
            </Box>
          )}
        </Box>
        {icon && (
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(color, 0.14),
              color,
              width: 42,
              height: 42,
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>
        )}
      </Stack>
    </Paper>
  );
};

const LegendDot = ({ color, label }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const statusColor = (status, theme) => {
  const s = (status || '').toLowerCase();
  if (s === 'complete') return theme.palette.success.main;
  if (s === 'cancelled') return theme.palette.error.main;
  if (s === 'no-show' || s === 'no show') return theme.palette.error.dark;
  if (s === 'waiting') return theme.palette.warning.main;
  if (s === 'arrived' || s === 'checked-in') return theme.palette.info.main;
  return theme.palette.text.secondary;
};

/* ---------- OPS view ---------- */

const OpsView = ({ snap, locationId }) => {
  const theme = useTheme();
  const ops = snap.ops;

  // Filter by selected location
  const filterLoc = (rows) =>
    locationId === ALL_LOCATIONS ? rows : rows.filter((r) => r.location_id === locationId);

  const byLocation = ops.today_by_location || [];
  const filteredByLocation = filterLoc(byLocation);
  const schedule = filterLoc(ops.schedule_today || []);
  const clinicians = filterLoc(ops.clinicians_today || []);

  const totals = filteredByLocation.reduce(
    (acc, l) => ({
      total: acc.total + num(l.total),
      completed: acc.completed + num(l.completed),
      waiting: acc.waiting + num(l.waiting),
      cancelled: acc.cancelled + num(l.cancelled),
      no_show: acc.no_show + num(l.no_show),
    }),
    { total: 0, completed: 0, waiting: 0, cancelled: 0, no_show: 0 },
  );

  const rev = ops.revenue_today;
  const invoiceDelta =
    num(rev.invoices_same_day_last_week) > 0
      ? Math.round(
          ((num(rev.invoices_total) - num(rev.invoices_same_day_last_week)) /
            num(rev.invoices_same_day_last_week)) *
            100,
        )
      : null;

  const leads = ops.leads;
  const leadDelta =
    num(leads.new_last_week) > 0
      ? Math.round(((num(leads.new_this_week) - num(leads.new_last_week)) / num(leads.new_last_week)) * 100)
      : null;
  const topSource = leads.sources_last_90d?.[0];
  const topSourceShare =
    topSource && leads.sources_last_90d
      ? Math.round(
          (num(topSource.count) /
            leads.sources_last_90d.reduce((a, s) => a + num(s.count), 0)) *
            100,
        )
      : 0;

  const tasks = ops.tasks;
  const health = ops.show_up_health;
  const noShowRate30 = pct(num(health.no_show_30d), num(health.total_30d));
  const noShowRate90 = pct(num(health.no_show_90d), num(health.total_90d));
  const cancelRate30 = pct(num(health.cancelled_30d), num(health.total_30d));
  const cancelRate90 = pct(num(health.cancelled_90d), num(health.total_90d));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Today · patient appts"
            value={totals.total}
            sub={
              totals.total > 0
                ? `${totals.completed} done · ${totals.waiting} waiting · ${totals.cancelled} cancelled · ${totals.no_show} no-show`
                : 'No appointments scheduled'
            }
            icon={<ScheduleIcon />}
            accent={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Today · revenue"
            value={formatCurrency(num(rev.invoices_total))}
            sub={`${rev.invoices_count} invoices raised · ${rev.payments_count} payments (${formatCurrency(num(rev.payments_total))})`}
            icon={<MoneyIcon />}
            accent={theme.palette.warning.main}
            delta={invoiceDelta}
            deltaPositiveGood
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="New leads · this week"
            value={num(leads.new_this_week)}
            sub={
              topSource
                ? `vs ${num(leads.new_last_week)} last week · top: ${topSource.source} (${topSourceShare}%)`
                : `vs ${num(leads.new_last_week)} last week`
            }
            icon={<PersonAddIcon />}
            accent={theme.palette.success.main}
            delta={leadDelta}
            deltaPositiveGood
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Overdue tasks (last 30d)"
            value={num(tasks.overdue_last_30d).toLocaleString()}
            sub={`${num(tasks.overdue_high_priority_30d)} high priority · ${num(tasks.due_today)} due today`}
            icon={<WarningIcon />}
            accent={theme.palette.error.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Today by location */}
        <Grid item xs={12} lg={5}>
          <InsightCard
            title="Today by location"
            subtitle={`${num(totals.total)} patient appointments across ${filteredByLocation.length} location${
              filteredByLocation.length === 1 ? '' : 's'
            }`}
            icon={<PlaceIcon fontSize="small" />}
            accent={theme.palette.info.main}
          >
            {filteredByLocation.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No patient appointments scheduled today at the selected location.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {byLocation
                  .filter((l) =>
                    locationId === ALL_LOCATIONS ? true : l.location_id === locationId,
                  )
                  .map((l) => {
                    const max = Math.max(...byLocation.map((x) => num(x.total)));
                    return (
                      <Box key={l.location_id}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {l.location_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {num(l.completed)} done · {num(l.waiting)} waiting ·{' '}
                            {num(l.cancelled)} cancelled
                            {num(l.no_show) > 0 ? ` · ${num(l.no_show)} no-show` : ''}
                          </Typography>
                        </Stack>
                        <Box sx={{ position: 'relative', height: 8 }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              height: 8,
                              width: `${(num(l.total) / max) * 100}%`,
                              bgcolor: alpha(theme.palette.info.main, 0.18),
                              borderRadius: 1,
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              height: 8,
                              width: `${(num(l.completed) / max) * 100}%`,
                              bgcolor: theme.palette.info.main,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
              </Stack>
            )}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <LegendDot color={theme.palette.info.main} label="Completed" />
              <LegendDot color={alpha(theme.palette.info.main, 0.18)} label="Booked" />
            </Stack>
          </InsightCard>
        </Grid>

        {/* Clinician utilization */}
        <Grid item xs={12} lg={7}>
          <InsightCard
            title="Clinician utilization today"
            subtitle={`${clinicians.length} staff with patient appts today`}
            icon={<OpsIcon fontSize="small" />}
            accent={theme.palette.primary.main}
          >
            {clinicians.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No clinicians scheduled with patient appointments today at the selected location.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {clinicians.map((c) => (
                  <Stack
                    key={c.id}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ py: 1.25 }}
                  >
                    <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontSize: '0.85rem', fontWeight: 600 }}>
                      {(c.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {c.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {c.job_title || '—'} · {c.location_name}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={`${num(c.appts_done)} done`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={`${num(c.appts_today)} total`}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </InsightCard>
        </Grid>

        {/* Today's schedule */}
        <Grid item xs={12}>
          <InsightCard
            title="Today's schedule"
            subtitle={`${schedule.length} patient appointments${
              locationId === ALL_LOCATIONS ? ' across all locations' : ''
            }`}
            icon={<CalendarIcon fontSize="small" />}
            accent={theme.palette.info.main}
          >
            {schedule.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No patient appointments today.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {schedule.map((a) => {
                  const start = parseISO(a.start_date);
                  const c = statusColor(a.status, theme);
                  return (
                    <Stack
                      key={a.id}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ py: 1.25 }}
                    >
                      <Box sx={{ width: 56 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {format(start, 'HH:mm')}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {a.client_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {a.service_name || '— service not set'} · {a.clinician_name || '— no clinician'}
                          {locationId === ALL_LOCATIONS ? ` · ${a.location_name}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        label={a.status || '—'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: alpha(c, 0.14),
                          color: c,
                          fontWeight: 600,
                          minWidth: 90,
                        }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </InsightCard>
        </Grid>

        {/* Show-up health */}
        {num(health.total_30d) > 0 && (
          <Grid item xs={12} lg={6}>
            <InsightCard
              title="Show-up health"
              subtitle={`Last 30 days · ${num(health.total_30d).toLocaleString()} appointments`}
              icon={<NoShowIcon fontSize="small" />}
              accent={theme.palette.warning.main}
            >
              <Stack spacing={2}>
                <RateRow label="No-show rate" value={noShowRate30} baseline={noShowRate90} higherIsWorse />
                <RateRow label="Cancellation rate" value={cancelRate30} baseline={cancelRate90} higherIsWorse />
                {(health.by_location_30d || []).length > 0 && (
                  <>
                    <Divider />
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      By location · last 30 days
                    </Typography>
                    <Stack spacing={1}>
                      {(health.by_location_30d || []).map((l) => {
                        const ns = pct(num(l.no_show), num(l.total));
                        const cn = pct(num(l.cancelled), num(l.total));
                        return (
                          <Stack key={l.location_id} direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {l.location_name}
                            </Typography>
                            <Stack direction="row" spacing={1.5}>
                              <Typography variant="caption" color="text.secondary">
                                {num(l.total)} appts
                              </Typography>
                              <Typography variant="caption" sx={{ color: ns >= noShowRate90 ? theme.palette.error.main : theme.palette.success.main, fontWeight: 600 }}>
                                {ns}% no-show
                              </Typography>
                              <Typography variant="caption" sx={{ color: cn >= cancelRate90 ? theme.palette.error.main : theme.palette.success.main, fontWeight: 600 }}>
                                {cn}% cancelled
                              </Typography>
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </Stack>
            </InsightCard>
          </Grid>
        )}

        {/* Action queue */}
        <Grid item xs={12} lg={6}>
          <InsightCard
            title="Action queue"
            subtitle="Tasks needing attention"
            icon={<TaskIcon fontSize="small" />}
            accent={theme.palette.error.main}
          >
            <Stack spacing={0}>
              <StatRow label="Overdue · high priority (last 30d)" value={num(tasks.overdue_high_priority_30d).toLocaleString()} accent={theme.palette.error.main} />
              <Divider />
              <StatRow label="Overdue · all (last 30d)" value={num(tasks.overdue_last_30d).toLocaleString()} accent={theme.palette.error.dark} />
              <Divider />
              <StatRow label="Due today" value={num(tasks.due_today).toLocaleString()} accent={theme.palette.warning.main} />
              <Divider />
              <StatRow label="Due this week" value={num(tasks.due_this_week).toLocaleString()} accent={theme.palette.info.main} />
              {num(tasks.overdue_all) > num(tasks.overdue_last_30d) && (
                <>
                  <Divider />
                  <StatRow
                    label="Backlog (older than 30d)"
                    value={(num(tasks.overdue_all) - num(tasks.overdue_last_30d)).toLocaleString()}
                    sub="excluded from the live count above"
                    accent={theme.palette.text.secondary}
                  />
                </>
              )}
            </Stack>
          </InsightCard>
        </Grid>

        {/* Lead pulse */}
        {(leads.sources_last_90d || []).length > 0 && (
          <Grid item xs={12}>
            <InsightCard
              title="Lead pulse"
              subtitle="Where new patients are coming from · last 90 days"
              icon={<CampaignIcon fontSize="small" />}
              accent={theme.palette.secondary.main}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <StatRow label="Open pipeline" value={num(leads.open_pipeline).toLocaleString()} accent={theme.palette.success.main} />
                  <Divider />
                  <StatRow label="New · this week" value={num(leads.new_this_week)} />
                  <Divider />
                  <StatRow label="New · last week" value={num(leads.new_last_week)} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={1.25}>
                    {leads.sources_last_90d.map((s) => {
                      const max = num(leads.sources_last_90d[0].count);
                      const share = pct(num(s.count), leads.sources_last_90d.reduce((a, x) => a + num(x.count), 0));
                      return (
                        <Box key={s.source}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {s.source}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {num(s.count).toLocaleString()} · {share}%
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={(num(s.count) / max) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.secondary.main, 0.08),
                              '& .MuiLinearProgress-bar': { bgcolor: theme.palette.secondary.main },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>
              </Grid>
            </InsightCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const StatRow = ({ label, value, sub, accent }) => (
  <Stack
    direction="row"
    alignItems="baseline"
    justifyContent="space-between"
    sx={{ py: 0.75 }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="body1" sx={{ fontWeight: 600, color: accent }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  </Stack>
);

const RateRow = ({ label, value, baseline, higherIsWorse }) => {
  const theme = useTheme();
  const diff = Number((value - baseline).toFixed(1));
  const worse = higherIsWorse ? diff > 0 : diff < 0;
  const color = worse ? theme.palette.error.main : theme.palette.success.main;
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {value.toFixed(1)}%
          </Typography>
          <DeltaPill value={diff} suffix="pp" positiveGood={!higherIsWorse} />
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        90-day baseline: {baseline.toFixed(1)}%
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(value * 3, 100)}
        sx={{
          mt: 0.75,
          height: 5,
          borderRadius: 1,
          bgcolor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { bgcolor: color },
        }}
      />
    </Box>
  );
};

/* ---------- OWNER view ---------- */

const OwnerView = ({ snap }) => {
  const theme = useTheme();
  const owner = snap.owner;
  const mtdDelta =
    num(owner.revenue_prior_month_same_day) > 0
      ? Math.round(
          ((num(owner.revenue_mtd) - num(owner.revenue_prior_month_same_day)) /
            num(owner.revenue_prior_month_same_day)) *
            100,
        )
      : null;
  const dailyMax = Math.max(1, ...((owner.daily_30d || []).map((d) => num(d.total))));
  const monthlyMax = Math.max(1, ...((owner.monthly_6m || []).map((d) => num(d.total))));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Revenue MTD"
            value={formatCurrency(num(owner.revenue_mtd))}
            sub={`vs ${formatCurrency(num(owner.revenue_prior_month_same_day))} same day last month`}
            icon={<MoneyIcon />}
            accent={theme.palette.success.main}
            delta={mtdDelta}
            deltaPositiveGood
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="AR · draft / unpaid"
            value={formatCurrency(num(owner.ar_outstanding?.draft_unpaid_total))}
            sub={`${num(owner.ar_outstanding?.draft_unpaid_count).toLocaleString()} invoices in draft / unpaid`}
            icon={<WarningIcon />}
            accent={theme.palette.error.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Avg invoice · last 30d"
            value={formatCurrency(num(owner.ar_outstanding?.avg_invoice_30d))}
            icon={<InsightsIcon />}
            accent={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiTile
            label="Open lead pipeline"
            value={num(snap.ops.leads.open_pipeline).toLocaleString()}
            sub={`${num(snap.ops.leads.new_this_week)} new this week`}
            icon={<TrendingUpIcon />}
            accent={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <InsightCard
            title="Revenue · last 30 days"
            subtitle="Daily totals · payments received"
            icon={<MoneyIcon fontSize="small" />}
            accent={theme.palette.success.main}
          >
            {(owner.daily_30d || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data.</Typography>
            ) : (
              <>
                <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 160 }}>
                  {owner.daily_30d.map((d) => (
                    <Tooltip key={d.day} title={`${d.day}: ${formatCurrency(num(d.total))}`}>
                      <Box
                        sx={{
                          flex: 1,
                          height: `${Math.max((num(d.total) / dailyMax) * 100, 2)}%`,
                          bgcolor: theme.palette.success.main,
                          opacity: 0.85,
                          borderRadius: '3px 3px 0 0',
                          '&:hover': { opacity: 1 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {owner.daily_30d[0]?.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {owner.daily_30d[owner.daily_30d.length - 1]?.day}
                  </Typography>
                </Stack>
              </>
            )}
          </InsightCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <InsightCard
            title="Monthly trend"
            subtitle="Last 6 months · payments"
            icon={<TrendingUpIcon fontSize="small" />}
            accent={theme.palette.primary.main}
          >
            <Stack spacing={1.5}>
              {(owner.monthly_6m || []).map((m) => (
                <Box key={m.month}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {format(parseISO(m.month), 'MMM yyyy')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(num(m.total))}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(num(m.total) / monthlyMax) * 100}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              ))}
            </Stack>
          </InsightCard>
        </Grid>

        {(owner.top_services_90d || []).length > 0 && (
          <Grid item xs={12}>
            <InsightCard
              title="Top services · last 90 days"
              subtitle="Services with non-zero billed revenue"
              icon={<InsightsIcon fontSize="small" />}
              accent={theme.palette.info.main}
            >
              <Stack spacing={2}>
                {owner.top_services_90d.map((s) => {
                  const max = num(owner.top_services_90d[0].revenue);
                  return (
                    <Box key={s.service_name}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {s.service_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(num(s.revenue))} · {num(s.appts)} appts
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(num(s.revenue) / max) * 100}
                        color="info"
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </InsightCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

/* ---------- MARKETING view ---------- */

const MarketingView = ({ snap }) => {
  const theme = useTheme();
  const leads = snap.ops.leads;
  const sources = leads.sources_last_90d || [];
  const trend = leads.weekly_trend_8w || [];
  const totalSources = sources.reduce((a, s) => a + num(s.count), 0);
  const trendMax = Math.max(1, ...trend.map((t) => num(t.new_leads)));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={4}>
          <KpiTile
            label="New leads · this week"
            value={num(leads.new_this_week)}
            sub={`vs ${num(leads.new_last_week)} last week`}
            icon={<PersonAddIcon />}
            accent={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <KpiTile
            label="Open pipeline"
            value={num(leads.open_pipeline).toLocaleString()}
            icon={<TrendingUpIcon />}
            accent={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <KpiTile
            label="Top source · last 90d"
            value={sources[0]?.source || '—'}
            sub={sources[0] ? `${pct(num(sources[0].count), totalSources)}% of new leads` : ''}
            icon={<CampaignIcon />}
            accent={theme.palette.secondary.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {trend.length > 0 && (
          <Grid item xs={12} lg={6}>
            <InsightCard
              title="New leads · weekly"
              subtitle="Last 8 weeks"
              icon={<TrendingUpIcon fontSize="small" />}
              accent={theme.palette.success.main}
            >
              <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ height: 160 }}>
                {trend.map((w) => (
                  <Tooltip key={w.week_start} title={`Week of ${w.week_start}: ${num(w.new_leads)} leads`}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: `${(num(w.new_leads) / trendMax) * 100}%`,
                          bgcolor: theme.palette.success.main,
                          opacity: 0.85,
                          borderRadius: '3px 3px 0 0',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {format(parseISO(w.week_start), 'd MMM')}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </InsightCard>
          </Grid>
        )}

        {sources.length > 0 && (
          <Grid item xs={12} lg={6}>
            <InsightCard
              title="Source mix · last 90 days"
              subtitle={`${num(totalSources).toLocaleString()} leads`}
              icon={<CampaignIcon fontSize="small" />}
              accent={theme.palette.secondary.main}
            >
              <Stack spacing={1.25}>
                {sources.map((s) => {
                  const max = num(sources[0].count);
                  return (
                    <Box key={s.source}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {s.source}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {num(s.count).toLocaleString()} · {pct(num(s.count), totalSources)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(num(s.count) / max) * 100}
                        color="secondary"
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </InsightCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

/* ---------- page shell ---------- */

const Dashboard = () => {
  const theme = useTheme();
  const [persona, setPersona] = useState('ops');
  const [locationId, setLocationId] = useState(ALL_LOCATIONS);

  const { data: snap, error, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['dashboard-ops'],
    queryFn: async () => {
      const res = await apiClient.get('/dashboard/ops');
      return res.data;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const today = format(new Date(), 'EEEE, d MMMM yyyy');
  const generated = snap?.generated_at
    ? formatDistanceToNow(parseISO(snap.generated_at), { addSuffix: true })
    : null;
  const err = error ? (error.response?.data?.detail || error.message || 'Failed to load') : null;

  const ActiveView = useMemo(() => {
    if (!snap) return null;
    if (persona === 'owner') return <OwnerView snap={snap} />;
    if (persona === 'marketing') return <MarketingView snap={snap} />;
    return <OpsView snap={snap} locationId={locationId} />;
  }, [persona, snap, locationId]);

  return (
    <Box>
      <Paper
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                {today}
              </Typography>
              {generated && (
                <Chip
                  label={`updated ${generated}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Clinic dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {persona === 'ops' && 'What needs your attention today across diary, leads and tasks.'}
              {persona === 'owner' && 'Revenue, AR and what drives the P&L.'}
              {persona === 'marketing' && 'Lead acquisition and source mix.'}
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              {persona === 'ops' && snap?.locations?.length > 0 && (
                <Select
                  size="small"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  sx={{ minWidth: 200, bgcolor: 'background.paper' }}
                  renderValue={(v) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PlaceIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                      <span>
                        {v === ALL_LOCATIONS
                          ? `All locations (${snap.locations.length})`
                          : snap.locations.find((l) => l.id === v)?.name || '—'}
                      </span>
                    </Stack>
                  )}
                >
                  <MenuItem value={ALL_LOCATIONS}>All locations ({snap.locations.length})</MenuItem>
                  {snap.locations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <ToggleButtonGroup
                value={persona}
                exclusive
                onChange={(_, v) => v && setPersona(v)}
                size="small"
                color="primary"
              >
                {PERSONAS.map((p) => (
                  <ToggleButton
                    key={p.value}
                    value={p.value}
                    sx={{ textTransform: 'none', fontWeight: 500, gap: 0.75 }}
                  >
                    {p.icon}
                    {p.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
                sx={{ textTransform: 'none' }}
              >
                {isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {err && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Couldn't load dashboard data: {err}
        </Alert>
      )}

      {isLoading && !snap ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
          <Grid item xs={12}><Skeleton variant="rounded" height={300} /></Grid>
        </Grid>
      ) : (
        ActiveView
      )}
    </Box>
  );
};

export default Dashboard;
